import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { IMG_GRADE, PRODUCTS, cardUrl } from '@/components/riffle/data'
import { clamp } from '@/components/riffle/math'

import {
    CARD_SHADOW,
    PARALLAX_EASE,
    PARALLAX_RANGE,
    PERSP,
    SHEEN,
    SPRING
} from '@/constants/riffle-ui'

// Five of the twenty-two, held in the coverflow pose the deck settles into.
// Same perspective, shadow, sheen, and spring constant as the section; the only
// difference is that this one answers the cursor instead of the scrollbar.

// Mist · Glow · Halo (the hero, centred) · Ember · Onyx
const PICKS = [3, 8, 0, 14, 10]
const MID = 2

interface CardState {
    x: number
    y: number
    z: number
    ry: number
    s: number
}

interface StripLayout {
    card: number
    gap: number
}

const measure = (): StripLayout => {
    const W = window.innerWidth
    const H = window.innerHeight
    const mobile = W < 700
    const card = mobile
        ? clamp(W * 0.26, 96, 150)
        : clamp(Math.min(W, H) * 0.19, 120, 190)
    return { card, gap: card * (mobile ? 0.66 : 0.84) }
}

const target = (i: number, L: StripLayout, lifted: boolean): CardState => {
    const d = i - MID
    const a = Math.abs(d)
    return {
        x: d * L.gap,
        y: a * L.card * 0.055 - (lifted ? L.card * 0.06 : 0),
        z: -a * 130 + (lifted ? 120 : 0),
        ry: -d * 15 * (lifted ? 0.35 : 1),
        s: 1 - a * 0.05 + (lifted ? 0.05 : 0)
    }
}

export const CardStrip: FC = (): ReactNode => {
    const sceneRef = useRef<HTMLDivElement>(null)
    const parallaxRef = useRef<HTMLDivElement>(null)
    const outerRefs = useRef<(HTMLDivElement | null)[]>([])
    const innerRefs = useRef<(HTMLDivElement | null)[]>([])
    const sheenRefs = useRef<(HTMLDivElement | null)[]>([])
    const labelRefs = useRef<(HTMLDivElement | null)[]>([])

    useEffect(() => {
        const scene = sceneRef.current
        const parallax = parallaxRef.current
        if (!scene || !parallax) return

        const outers = outerRefs.current
        const inners = innerRefs.current
        const sheens = sheenRefs.current
        const labels = labelRefs.current

        let L = measure()
        let hover = -1
        const cur: CardState[] = PICKS.map((_, i) => target(i, L, false))

        const applySizes = (): void => {
            const c = L.card
            // room for the tallest card plus its label, both sides of centre
            scene.style.height = `${Math.round(c * 1.3 + 96)}px`
            for (let i = 0; i < PICKS.length; i++) {
                const o = outers[i]
                if (o) {
                    o.style.width = `${c}px`
                    o.style.height = `${c}px`
                    o.style.marginLeft = `${-c / 2}px`
                    o.style.marginTop = `${-c / 2}px`
                }
                const inn = inners[i]
                if (inn) inn.style.borderRadius = `${Math.round(c * 0.09)}px`
            }
        }

        const write = (i: number, p: CardState): void => {
            const o = outers[i]
            if (o) {
                o.style.transform =
                    `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, ${p.z.toFixed(2)}px) ` +
                    `rotateY(${p.ry.toFixed(2)}deg) scale(${p.s.toFixed(3)})`
                o.style.zIndex = `${Math.round(1000 + p.z)}`
            }
            const sh = sheens[i]
            if (sh) {
                const k = clamp(p.ry / 54, -1, 1)
                sh.style.transform = `translateX(${(k * 42).toFixed(2)}%)`
                sh.style.opacity = `${0.08 + Math.abs(k) * 0.26}`
            }
            const lab = labels[i]
            if (lab) {
                lab.style.transform = `translateX(-50%) rotateY(${(-p.ry).toFixed(2)}deg)`
            }
        }

        applySizes()
        cur.forEach((p, i) => write(i, p))

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduce) {
            inners.forEach((el) => {
                if (el) el.style.opacity = '1'
            })
            labels.forEach((el) => {
                if (el) el.style.opacity = '1'
            })

            const onResizeStatic = (): void => {
                L = measure()
                applySizes()
                for (let i = 0; i < PICKS.length; i++) {
                    const p = target(i, L, false)
                    Object.assign(cur[i], p)
                    write(i, p)
                }
            }

            window.addEventListener('resize', onResizeStatic)
            return () => window.removeEventListener('resize', onResizeStatic)
        }

        // ─── Entrance bloom, the deck's own ───
        gsap.registerPlugin(ScrollTrigger)

        const ctx = gsap.context(() => {
            const list = inners.filter(Boolean) as HTMLDivElement[]
            const tags = labels.filter(Boolean) as HTMLDivElement[]

            gsap.fromTo(
                list,
                { opacity: 0, scale: 0.62, filter: 'blur(10px)' },
                {
                    opacity: 1,
                    scale: 1,
                    filter: 'blur(0px)',
                    duration: 1.05,
                    ease: 'power4.out',
                    stagger: { each: 0.06, from: 'center' },
                    scrollTrigger: { trigger: scene, start: 'top 82%', once: true }
                }
            )

            gsap.fromTo(
                tags,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 0.6,
                    ease: 'power2.out',
                    stagger: { each: 0.06, from: 'center' },
                    delay: 0.34,
                    scrollTrigger: { trigger: scene, start: 'top 82%', once: true }
                }
            )
        }, scene)

        // ─── Per-frame springs ───
        const par = { x: 0, y: 0, tx: 0, ty: 0 }
        let raf = 0

        const frame = (): void => {
            raf = requestAnimationFrame(frame)

            par.x += (par.tx - par.x) * PARALLAX_EASE
            par.y += (par.ty - par.y) * PARALLAX_EASE
            parallax.style.transform =
                `rotateX(${par.x.toFixed(2)}deg) rotateY(${par.y.toFixed(2)}deg)`

            for (let i = 0; i < PICKS.length; i++) {
                const t = target(i, L, i === hover)
                const c = cur[i]
                c.x += (t.x - c.x) * SPRING
                c.y += (t.y - c.y) * SPRING
                c.z += (t.z - c.z) * SPRING
                c.ry += (t.ry - c.ry) * SPRING
                c.s += (t.s - c.s) * SPRING
                write(i, c)
            }
        }
        raf = requestAnimationFrame(frame)

        const hitTest = (x: number, y: number): number => {
            let best = -1
            let bestZ = -Infinity
            for (let i = 0; i < PICKS.length; i++) {
                const el = inners[i]
                if (!el) continue
                const r = el.getBoundingClientRect()
                if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom && cur[i].z > bestZ) {
                    bestZ = cur[i].z
                    best = i
                }
            }
            return best
        }

        const onMove = (e: PointerEvent): void => {
            const r = scene.getBoundingClientRect()
            par.ty = ((e.clientX - r.left) / r.width - 0.5) * PARALLAX_RANGE
            par.tx = (0.5 - (e.clientY - r.top) / r.height) * PARALLAX_RANGE
            hover = hitTest(e.clientX, e.clientY)
        }

        const onLeave = (): void => {
            par.tx = 0
            par.ty = 0
            hover = -1
        }

        const onResize = (): void => {
            L = measure()
            applySizes()
            ScrollTrigger.refresh()
        }

        scene.addEventListener('pointermove', onMove)
        scene.addEventListener('pointerleave', onLeave)
        window.addEventListener('resize', onResize)

        return () => {
            cancelAnimationFrame(raf)
            ctx.revert()
            scene.removeEventListener('pointermove', onMove)
            scene.removeEventListener('pointerleave', onLeave)
            window.removeEventListener('resize', onResize)
        }
    }, [])

    return (
        <div
            ref={sceneRef}
            className='relative w-full overflow-hidden'
            style={{ perspective: `${PERSP}px`, perspectiveOrigin: '50% 50%', height: '46vh' }}
        >
            <div
                ref={parallaxRef}
                className='absolute inset-0 flex items-center justify-center'
                style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
            >
                <div
                    className='relative'
                    style={{ width: 0, height: 0, transformStyle: 'preserve-3d' }}
                >
                    {PICKS.map((p, i) => (
                        <div
                            key={p}
                            ref={(el) => {
                                outerRefs.current[i] = el
                            }}
                            className='absolute left-1/2 top-1/2'
                            style={{
                                transformStyle: 'preserve-3d',
                                pointerEvents: 'none',
                                willChange: 'transform'
                            }}
                        >
                            <div
                                ref={(el) => {
                                    innerRefs.current[i] = el
                                }}
                                className='relative h-full w-full overflow-hidden'
                                style={{
                                    background: '#15151a',
                                    opacity: 0,
                                    boxShadow: CARD_SHADOW,
                                    willChange: 'opacity, transform, filter'
                                }}
                            >
                                { /* eslint-disable-next-line @next/next/no-img-element */ }
                                <img
                                    src={cardUrl(p + 1)}
                                    alt={`${PRODUCTS[p].title} — ${PRODUCTS[p].artist}`}
                                    loading='lazy'
                                    draggable={false}
                                    className='absolute inset-0 h-full w-full object-cover'
                                    style={{ filter: IMG_GRADE }}
                                />

                                <div
                                    ref={(el) => {
                                        sheenRefs.current[i] = el
                                    }}
                                    aria-hidden
                                    className='pointer-events-none absolute inset-0'
                                    style={{ background: SHEEN, mixBlendMode: 'overlay', opacity: 0 }}
                                />
                            </div>

                            <div
                                ref={(el) => {
                                    labelRefs.current[i] = el
                                }}
                                className='absolute left-1/2 text-center'
                                style={{ top: '100%', marginTop: 14, opacity: 0, whiteSpace: 'nowrap' }}
                            >
                                <div
                                    style={{
                                        fontSize: '12.5px',
                                        fontWeight: 500,
                                        letterSpacing: '0.01em',
                                        color: '#fff',
                                        textShadow: '0 2px 12px rgba(0,0,0,0.7)'
                                    }}
                                >
                                    {PRODUCTS[p].title}
                                </div>

                                <div
                                    className='font-mono uppercase'
                                    style={{
                                        fontSize: '8.5px',
                                        letterSpacing: '0.28em',
                                        opacity: 0.55,
                                        marginTop: 2,
                                        color: '#fff'
                                    }}
                                >
                                    {PRODUCTS[p].artist}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default CardStrip
